// The single owner of access-token lifecycle for the whole SPA. Both transports
// — the REST client and the realtime WebSocket — get their token from here, so
// there is exactly ONE refresh in flight at a time. That matters because refresh
// tokens are single-use and rotate on every call: two independent refreshers
// (one per transport) would race, the loser replaying a consumed token and
// forcing a spurious sign-out.
//
// It refreshes PROACTIVELY: `validAccessToken()` refreshes before returning a
// token that is expired or within the skew window, so a request never has to
// fail first — and, crucially, a WebSocket handshake (which can't report *why*
// it was rejected) is never attempted with a stale token.
import type { TokenPair } from "@/types";
import { tokenStore } from "./tokenStore";
import { expireSession } from "./session";

const AUTH_REFRESH_PATH = "/auth/refresh";
// Refresh this long before the real expiry. Covers request/handshake latency and
// stays within the backend's 60s JWT leeway, so the token is never presented dead.
const EXPIRY_SKEW_MS = 60_000;
// Only these statuses mean the refresh token is genuinely rejected and the session
// is over. Everything else (5xx, 429, a network error from a cold Lambda) is
// transient: we retry, and if still failing we surface the error WITHOUT signing
// the user out — a flaky backend must not look like a revoked session.
const TERMINAL_STATUSES = new Set([401, 403]);
const MAX_REFRESH_ATTEMPTS = 3;
const RETRY_BACKOFF_MS = 500;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** What token consumers (REST client, WebSocket) depend on. */
export interface TokenProvider {
  /** A valid access token, refreshing proactively if near/after expiry. Null when signed out. */
  validAccessToken(): Promise<string | null>;
  /** Force a refresh after a server rejection; the new token, or null if unrecoverable. */
  forceRefresh(): Promise<string | null>;
}

export class AuthSession implements TokenProvider {
  // Single-flight guard shared by every caller (see file header).
  private refreshInFlight: Promise<string | null> | null = null;

  constructor(private readonly baseUrl: string) {}

  async validAccessToken(): Promise<string | null> {
    const token = tokenStore.get();
    if (!token) return null;
    return this.isFresh() ? token : this.forceRefresh();
  }

  forceRefresh(): Promise<string | null> {
    this.refreshInFlight ??= this.rotateTokens().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  /** True when the current access token has comfortable life left. */
  private isFresh(): boolean {
    const expiresAt = tokenStore.getExpiresAt();
    // Unknown expiry (e.g. a token stored before this field existed): treat as
    // stale so we refresh once and start tracking expiry, rather than trust it.
    if (expiresAt === null) return false;
    return Date.now() < expiresAt - EXPIRY_SKEW_MS;
  }

  /**
   * Exchange the stored refresh token for a fresh pair.
   * - Success → the new access token.
   * - Terminal rejection (401/403) → end the session, return null.
   * - Transient failure (5xx/429/network) → retry, then THROW so the caller can
   *   back off and retry later; the session is preserved.
   */
  private async rotateTokens(): Promise<string | null> {
    const refreshToken = tokenStore.getRefresh();
    if (!refreshToken) {
      expireSession();
      return null;
    }
    let lastError: unknown;
    for (let attempt = 0; attempt < MAX_REFRESH_ATTEMPTS; attempt++) {
      if (attempt > 0) await delay(RETRY_BACKOFF_MS * attempt);
      let res: Response;
      try {
        res = await fetch(`${this.baseUrl}${AUTH_REFRESH_PATH}`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
      } catch (err) {
        lastError = err; // offline / DNS / TLS — transient, retry
        continue;
      }
      if (res.ok) {
        const pair = (await res.json()) as TokenPair;
        tokenStore.setPair(pair);
        return pair.accessToken;
      }
      if (TERMINAL_STATUSES.has(res.status)) {
        expireSession();
        return null;
      }
      lastError = new Error(`token refresh failed: ${res.status}`);
    }
    // Exhausted retries on transient failures — keep the session, let the caller decide.
    throw lastError instanceof Error ? lastError : new Error("token refresh failed");
  }
}
